import Config from "assets/configs/score.js"
import RadarChart from "components/widgets/ScorePage/widgets/RadarChart"
import {
  TextChart,
  TextChartMajor,
  TextChartMinor,
} from "components/widgets/ScorePage/widgets/TextCharts"
import PropTypes from "prop-types"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import HelpWidgetChartInteractiveMolecule from "views/molecules/interactive/HelpWidgetChartInteractiveMolecule"
import ChartBadge from "views/atoms/badges/ChartBadge"
import ScoreLayoutOrganism from "views/organisms/scores/ScoreLayoutOrganism"
import { COLORS } from "utils/constants/colors"
import AvgCumulativeBarChartTemplate from "views/molecules/charts/template/AvgCumulativeBarChartTemplate"
import RadarChartTemplate from "views/molecules/charts/template/RadarChartTemplate"
import { useAppContext } from "components/AppContext"
import ChartWithBadgeWrapperMolecule from "views/molecules/charts/ChartWithBadgeWrapperMolecule"
import ScoreChartErrorBoundary from "components/ErrorBoundary/ScoreChartErrorBoundary"

const color = (value) => {
  if (value < 33) return COLORS.danger.primary
  if (33 < value && value < 66) return COLORS.warning.primary
  return COLORS.planet.primary
}

const Objective12Score = ({ score, collect }) => {
  const { t } = useTranslation()
  const { score: globalScore } = useAppContext()
  const translationPrefix = "score:score.12."
  const useNewOdd12Kpis = (score?.odd12_kpi_methodology_version ?? 1) >= 2

  const chart1TrendBadge = useNewOdd12Kpis
    ? globalScore?.trend_badges?.odd12_refurbished_purchase_ratio
    : globalScore?.trend_badges?.odd12_sustainable_production_reuse_rate
  const chart2TrendBadge = useNewOdd12Kpis
    ? globalScore?.trend_badges?.odd12_leasing_ratio
    : globalScore?.trend_badges?.odd12_sustainable_consumption_label_maturity
  const chart5TrendBadge = useNewOdd12Kpis
    ? globalScore?.trend_badges?.odd12_eol_refurbishing_ratio
    : globalScore?.trend_badges?.odd12_electronic_waste_recycled_ratio
  const maturityTrendBadge = globalScore?.trend_badges?.odd12_maturity

  const workplace_equipments = [
    "desktop",
    "laptop",
    "tablet",
    "smartphone",
    "monitor",
  ]

  const differentiateLabels = (labels, domains) => {
    const labelCount = labels.reduce((acc, label) => {
      acc[label] = (acc[label] || 0) + 1
      return acc
    }, {})

    return labels.map((label, index) => {
      if (labelCount[label] > 1) {
        return `${label} (${domains[index]})`
      }
      return label
    })
  }

  const chart1 = useCallback(() => {
    if (useNewOdd12Kpis && !score?.has_equipment_inventories) {
      return <ScoreChartErrorBoundary />
    }

    const kpi = useNewOdd12Kpis
      ? score["odd12_refurbished_purchase_ratio"]
      : score["odd12_sustainable_production_reuse_rate"]

    const chartKey = useNewOdd12Kpis ? "chart1." : "chart1.legacy."

    const content = {
      kpi,
      kpi_unit: t(translationPrefix + chartKey + "unit"),
      kpi_text: t(translationPrefix + chartKey + "text"),
      translationPrefix_chartX: translationPrefix + chartKey,
    }

    const content_for_display = <TextChartMajor content={content} />

    const chartContent = (
      <TextChart content={content_for_display} />
    )

    return (
      <HelpWidgetChartInteractiveMolecule
        text={t(translationPrefix + chartKey + "help")}
      >
        <ChartWithBadgeWrapperMolecule trendBadge={chart1TrendBadge} color="auto-inverse" >
          {chartContent}
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }, [score, chart1TrendBadge, useNewOdd12Kpis])

  const chart2 = useCallback(() => {
    const chartKey = useNewOdd12Kpis ? "chart2." : "chart2.legacy."

    if (useNewOdd12Kpis && !score?.has_equipment_inventories) {
      return <ScoreChartErrorBoundary />
    }

    const kpi = useNewOdd12Kpis
      ? score["odd12_leasing_ratio"]
      : score["odd12_sustainable_consumption_label_maturity"]

    const content = useNewOdd12Kpis
      ? {
          reference: (
            <ChartBadge
              value={score["odd12_leasing_ratio_workplace"]}
              lowerLimit={30}
              upperLimit={70}
            />
          ),
          kpi,
          kpi_unit: t(translationPrefix + chartKey + "unit"),
          kpi_text: t(translationPrefix + chartKey + "text"),
          sub_kpi: score["odd12_leasing_ratio_workplace"],
          sub_kpi_unit: t(translationPrefix + chartKey + "unit"),
          sub_kpi_text: t(translationPrefix + chartKey + "sub_kpi_text"),
        }
      : {
          reference: (
            <ChartBadge value={kpi} lowerLimit={30} upperLimit={70} />
          ),
          kpi,
          kpi_unit: t(translationPrefix + chartKey + "unit"),
          kpi_text: t(translationPrefix + chartKey + "text"),
        }

    const content_for_display = <TextChartMinor content={content} />

    const chartContent = (
      <TextChart content={content_for_display} />
    )

    return (
      <HelpWidgetChartInteractiveMolecule
        text={t(translationPrefix + chartKey + "help")}
      >
        <ChartWithBadgeWrapperMolecule trendBadge={chart2TrendBadge} color="auto-inverse" >
          {chartContent}
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }, [score, chart2TrendBadge, useNewOdd12Kpis])

  // version zoom per equipment
  const chart3a = useCallback(() => {
    let datas = []
    let labels = [
      t(translationPrefix + "chart3.second_hand"),
      t(translationPrefix + "chart3.reuse"),
      t(translationPrefix + "chart3.internal_reconditioning"),
      t(translationPrefix + "chart3.external_reconditioning"),
    ]
    let dataset = []

    workplace_equipments.forEach((workplace_equipment) => {
      collect["collected_equipments"].forEach((equipment) => {
        if (workplace_equipment == equipment["equipment_type"]) {
          datas.push(Number(equipment["second_hand_ratio"]) ?? 0)
          datas.push(Number(equipment["internal_reuse_ratio"]) ?? 0)
          datas.push(
            Number(equipment["eol_internal_reconditioning_ratio"]) ?? 0
          )
          datas.push(
            Number(equipment["eol_external_reconditioning_ratio"]) ?? 0
          )

          let object = {
            label: t(`common:equipment_types.${workplace_equipment}`),
            data: { datas },
          }
          dataset.push(object)
          datas = []
        }
      })
    })

    let data = [
      {
        labels: labels,
        datasets: dataset,
      },
    ]

    return (
      <RadarChart title={t(translationPrefix + "chart3.title")} data={data} />
    )
  }, [score])

  // version zoom per end of life
  const chart3 = useCallback(() => {
    if (!score?.has_equipment_inventories) {
      return <ScoreChartErrorBoundary />
    }

    const translatedLabels = score.second_hand_vs_reuse.labels.map((label) =>
      t(`common:equipment_types.${label}`)
    )

    const translatedDomains = score.second_hand_vs_reuse.domains.map((domain) =>
      t(`common:domains.${domain}`)
    )

    const labels = differentiateLabels(translatedLabels, translatedDomains)

    score.second_hand_vs_reuse.datasets.forEach((dataset) => {
      dataset.label = t(`score:score.12.chart3.${dataset.label_key}`)
    })

    const data = score.second_hand_vs_reuse.datasets.map((dataset, index) => {
      const pointColor = (index, data) => {
        return data.map(color)
      }
      const pointBackgroundColors = pointColor(index, dataset.data.datas)
      return {
        label: dataset.label,
        data: dataset.data.datas,
        pointBackgroundColor: pointBackgroundColors,
      }
    })

    return (
      <RadarChartTemplate
        title={t(translationPrefix + "chart3.title")}
        datasets={data}
        labelKeys={labels}
        chartFill
        usePointBackgroundColorInDatasets
        helpText={t(translationPrefix + "chart3.help")}
      />
    )
  }, [score])

  const chart4 = useCallback(() => {
    const {
      datasets,
      labels,
      references,
      domains,
    } = score.odd12_collected_equipments

    const dataset = {
      datasets: datasets.map((dataset) => {
        return {
          data: dataset.data,
          label: t(translationPrefix + `${dataset.label}`),
          backgroundColor: dataset.backgroundColor,
        }
      }),
    }

    const translatedLabels = labels.map((label, index) => {
      const equipmentType = label[1]
      const hasSameEquipmentType =
        labels.filter((label) => label[1] === equipmentType).length > 1

      return !hasSameEquipmentType
        ? t(`common:equipment_types.${label[1]}`)
        : `${t(`common:equipment_types.${label[1]}`)} (${t(
            `common:domains.${domains[index]}`
          )})`
    })

    return (
      <AvgCumulativeBarChartTemplate
        title={t(translationPrefix + "chart4.title")}
        datasets={dataset.datasets}
        useBackgroundColorsInDatasets
        referenceData={references}
        labelKeys={translatedLabels}
        unitPath={`${translationPrefix}chart4`}
        unitKey={"data_title"}
        helpText={t(translationPrefix + "chart4.help")}
      />
    )
  }, [score])

  const chart5 = useCallback(() => {
    if (!score?.has_equipment_inventories) {
      return <ScoreChartErrorBoundary />
    }

    const chartKey = useNewOdd12Kpis ? "chart5." : "chart5.legacy."

    const kpi = useNewOdd12Kpis
      ? score["odd12_eol_refurbishing_ratio"]
      : score["odd15_electronic_waste_non_recycled_ratio"] !== null
        ? 100 - score["odd15_electronic_waste_non_recycled_ratio"]
        : t(`score:score.12.chart5.legacy.not_available`)

    const content = useNewOdd12Kpis
      ? {
          reference: (
            <ChartBadge value={kpi} lowerLimit={30} upperLimit={70} />
          ),
          kpi,
          kpi_unit: t(translationPrefix + chartKey + "unit"),
          kpi_text: t(translationPrefix + chartKey + "text"),
        }
      : {
          reference: (
            <ChartBadge value={kpi} lowerLimit={80} upperLimit={95} />
          ),
          kpi,
          kpi_unit: kpi !== "N/A" ? t(translationPrefix + chartKey + "unit") : "",
          kpi_text: t(translationPrefix + chartKey + "text"),
        }

    const content_for_display = <TextChartMinor content={content} />

    const chartContent = (
      <TextChart content={content_for_display} />
    )

    return (
      <HelpWidgetChartInteractiveMolecule
        text={t(translationPrefix + chartKey + "help")}
      >
        <ChartWithBadgeWrapperMolecule trendBadge={chart5TrendBadge} color="auto-inverse" >
          {chartContent}
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }, [score, chart5TrendBadge, useNewOdd12Kpis])

  const chart6 = useCallback(() => {
    if (!score?.has_equipment_inventories) {
      return <ScoreChartErrorBoundary />
    }

    const endOfLife = score.end_of_life
    const translatedLabels = endOfLife.labels.map((label) =>
      t(`common:equipment_types.${label}`)
    )

    const translatedDomains = endOfLife.domains.map((domain) =>
      t(`common:domains.${domain}`)
    )

    const labels = differentiateLabels(translatedLabels, translatedDomains)

    endOfLife.datasets.forEach((dataset) => {
      dataset.label = t(`score:score.12.chart6.${dataset.label_key}`)
      dataset.backgroundColor =
        Config["12"].chart6[dataset.label_key].backgroundColor

      const color = Config["12"].chart6[dataset.label_key].borderColor
      dataset.borderColor = color
      dataset.pointBackgroundColor = Array(dataset.data.datas.length).fill(
        color
      )
    })

    const data = endOfLife.datasets.map((dataset) => {
      return {
        label: dataset.label,
        data: dataset.data.datas,
        backgroundColor: dataset.backgroundColor,
        borderColor: dataset.borderColor,
        pointBackgroundColor: dataset.pointBackgroundColor,
      }
    })

    return (
      <RadarChartTemplate
        title={t(translationPrefix + "chart6.title")}
        datasets={data}
        gapSize={10}
        labelKeys={labels}
        chartFill
        useBackgroundColorsInDatasets
        useBorderColorInDatasets
        usePointBackgroundColorInDatasets
        helpText={t(translationPrefix + "chart6.help")}
      />
    )
  }, [score])

  return (
    <ScoreLayoutOrganism
      maturity={score.odd12_maturity ?? 0}
      maturityTrendBadge={maturityTrendBadge}
      chart1={chart1()}
      chart2={chart2()}
      chart3={chart3()}
      chart4={chart4()}
      chart5={chart5()}
      chart6={chart6()}
    />
  )
}

Objective12Score.propTypes = {
  score: PropTypes.object.isRequired,
  collect: PropTypes.object.isRequired,
}

export default Objective12Score